import type { ItemView, WeekResponse } from '@fall26/shared'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useCourses, useSetDone, useWeek } from '../api/hooks.js'
import { ItemSheet } from '../components/item/ItemSheet.js'
import { DayPanel } from '../components/week/DayPanel.js'

/**
 * This Week.
 *
 * SEVEN days, always fetched. Desktop shows Monday to Friday and reveals the
 * weekend only when it carries something — which happens exactly once all
 * semester, on Saturday 12 December, the date of ME 334's final. Dropping the
 * weekend to make a tidy five-column grid would hide the single heaviest item
 * on the schedule.
 *
 * The phone is not that grid made narrow. It is a vertical stack with today
 * open and the rest folded away, because a week of five columns on a 420px
 * screen is unreadable and scrolling past six closed days to reach Thursday is
 * faster than pinching at a squeezed one.
 */
export function Week() {
  const { date } = useParams()
  const week = useWeek(date ?? 'today')
  const courses = useCourses()
  const setDone = useSetDone()
  const navigate = useNavigate()
  const [editing, setEditing] = useState<number | null>(null)

  const hue = useMemo(() => {
    const map = new Map<string, number>((courses.data?.courses ?? []).map((c) => [String(c.id), c.colorIndex]))
    return (courseId: string) => map.get(courseId) ?? 0
  }, [courses.data])

  if (week.isError) {
    return (
      <p className="rounded-[var(--radius)] border border-[var(--overdue)]/30 bg-[var(--overdue-wash)] p-4 text-sm text-[var(--overdue)]">
        Could not load the week.
      </p>
    )
  }

  // Guard on the data, not on isLoading: between retries a query is neither
  // loading nor errored and still has nothing.
  if (!week.data) return <Skeleton />

  const data = week.data
  const toggle = (item: ItemView, done: boolean) => setDone.mutate({ itemId: item.id, done })
  // The week's rows come from seven day lists rather than one array, so the
  // sheet looks its subject up by id instead of holding a copy that would go
  // stale the moment the save invalidates the query.
  const editingItem = editing === null ? null : data.days.flatMap((d) => d.due).find((i) => i.id === editing) ?? null

  return (
    <section className="space-y-4">
      <LabCard lab={data.lab} />

      <WeekNav data={data} onGo={(d) => navigate(`/week/${d}`)} />

      {/* phone: a stack, today open ------------------------------------- */}
      <div className="md:hidden">
        <DayStack data={data} hue={hue} onToggle={toggle} onOpen={(i) => setEditing(i.id)} />
      </div>

      {/* desktop: columns ------------------------------------------------ */}
      <div className="hidden md:block">
        <DayColumns data={data} hue={hue} onToggle={toggle} onOpen={(i) => setEditing(i.id)} />
      </div>

      {editingItem && <ItemSheet item={editingItem} onClose={() => setEditing(null)} />}
    </section>
  )
}

function LabCard({ lab }: { lab: WeekResponse['lab'] }) {
  return (
    <div
      className={`rounded-[var(--radius-lg)] border p-4 ${
        lab.hasLab
          ? 'border-[var(--accent-line)] bg-[var(--accent-wash)]'
          : 'border-[var(--border)] bg-[var(--surface)]'
      }`}
    >
      <div className="tabular text-[10px] uppercase tracking-[0.18em] text-[var(--text-subtle)]">
        Do I have lab this week?
      </div>
      {/* The server answers this in words. The client renders the sentence and
          assembles nothing. */}
      <p
        className={`mt-1.5 text-[15px] leading-snug ${
          lab.hasLab ? 'font-medium text-[var(--text)]' : 'text-[var(--text-muted)]'
        }`}
      >
        {lab.sentence}
      </p>
    </div>
  )
}

function WeekNav({ data, onGo }: { data: WeekResponse; onGo: (date: string) => void }) {
  const step = (days: number) => {
    const d = new Date(`${data.weekStart}T12:00:00Z`)
    d.setUTCDate(d.getUTCDate() + days)
    onGo(d.toISOString().slice(0, 10))
  }
  const showsToday = data.today >= data.weekStart && data.today <= data.weekEnd

  return (
    <div className="flex items-center gap-2">
      <button type="button" onClick={() => step(-7)} aria-label="Previous week" className={navButton}>
        ←
      </button>
      <div className="min-w-0 flex-1 text-center">
        <div className="tabular text-[11px] text-[var(--text-muted)]">
          {data.weekStart} → {data.weekEnd}
        </div>
      </div>
      {!showsToday && (
        <Link to="/" className={`${navButton} !w-auto px-3 text-[11px] uppercase tracking-[0.08em]`}>
          Today
        </Link>
      )}
      <button type="button" onClick={() => step(7)} aria-label="Next week" className={navButton}>
        →
      </button>
    </div>
  )
}

const navButton =
  'tap flex w-11 items-center justify-center rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] text-[var(--text-muted)]'

/** Phone: today open, the rest folded. */
function DayStack({
  data,
  hue,
  onToggle,
  onOpen,
}: {
  data: WeekResponse
  hue: (id: string) => number
  onToggle: (item: ItemView, done: boolean) => void
  onOpen: (item: ItemView) => void
}) {
  const busiest = data.days.find((d) => d.due.length > 0)?.date ?? data.days[0]?.date
  const initial = data.days.some((d) => d.isToday) ? data.today : busiest
  const [open, setOpen] = useState<string | null>(initial ?? null)

  // Moving to another week should open that week's today, or its first day
  // with anything on it — not hold a date that is no longer on screen.
  useEffect(() => setOpen(initial ?? null), [initial])

  return (
    <div className="divide-y divide-[var(--rule)] overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)]">
      {data.days.map((day) => {
        const isOpen = open === day.date
        const count = day.due.length
        const meets = day.meetings.filter((m) => m.meets).length

        return (
          <div key={day.date}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : day.date)}
              aria-expanded={isOpen}
              className={`tap flex w-full items-center gap-3 px-3 py-2.5 text-left ${
                day.isToday ? 'bg-[var(--accent-wash)]' : ''
              }`}
            >
              <span
                aria-hidden
                className="w-[3px] self-stretch rounded-full"
                style={{ backgroundColor: day.isToday ? 'var(--accent)' : 'transparent' }}
              />
              <span
                className={`tabular w-[92px] shrink-0 text-[12px] ${
                  day.isToday
                    ? 'font-semibold text-[var(--accent)]'
                    : day.isPast
                      ? 'text-[var(--text-subtle)]'
                      : 'text-[var(--text)]'
                }`}
              >
                {day.label}
              </span>

              <span className="min-w-0 flex-1 truncate text-[12px] text-[var(--text-muted)]">
                {day.noClassReason ? (
                  <span className="text-[var(--text-subtle)]">{day.noClassReason}</span>
                ) : count > 0 ? (
                  <>
                    <span className="tabular font-medium text-[var(--text)]">{count}</span> due
                    {meets > 0 && <span className="text-[var(--text-subtle)]"> · {meets} class</span>}
                  </>
                ) : meets > 0 ? (
                  <span className="text-[var(--text-subtle)]">{meets} class</span>
                ) : (
                  <span className="text-[var(--text-subtle)]">—</span>
                )}
              </span>

              {day.isFinalExamDay && <Tag tone="overdue">final</Tag>}
              {day.isStudyDay && <Tag tone="unknown">study</Tag>}
              <span aria-hidden className="tabular text-[11px] text-[var(--text-subtle)]">
                {isOpen ? '−' : '+'}
              </span>
            </button>

            {isOpen && (
              <div className="px-3 pt-1 pb-3">
                <DayPanel day={day} hue={hue} onToggle={onToggle} onOpen={onOpen} />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** Desktop: Monday to Friday, plus the weekend when it carries anything. */
function DayColumns({
  data,
  hue,
  onToggle,
  onOpen,
}: {
  data: WeekResponse
  hue: (id: string) => number
  onToggle: (item: ItemView, done: boolean) => void
  onOpen: (item: ItemView) => void
}) {
  const weekend = data.days.slice(5)
  const weekendHasContent = weekend.some(
    (d) => d.due.length > 0 || d.meetings.some((m) => m.meets) || d.isFinalExamDay,
  )
  const shown = weekendHasContent ? data.days : data.days.slice(0, 5)

  return (
    <div>
      {weekendHasContent && (
        <p className="tabular mb-2 text-[11px] text-[var(--soon)]">
          Weekend shown — something falls on it this week.
        </p>
      )}
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${shown.length}, minmax(0, 1fr))` }}
      >
        {shown.map((day) => (
          <div
            key={day.date}
            className={`rounded-[var(--radius)] border p-2.5 ${
              day.isToday
                ? 'border-[var(--accent-line)] bg-[var(--accent-wash)]'
                : isWeekend(day.weekday)
                  ? 'border-[var(--border)] bg-[var(--surface-sunken)]'
                  : 'border-[var(--border)] bg-[var(--surface)]'
            }`}
          >
            <div className="mb-2 flex items-baseline justify-between gap-1">
              <span
                className={`tabular text-[11px] ${
                  day.isToday
                    ? 'font-semibold text-[var(--accent)]'
                    : day.isPast
                      ? 'text-[var(--text-subtle)]'
                      : 'text-[var(--text)]'
                }`}
              >
                {day.label}
              </span>
              {day.isFinalExamDay && <Tag tone="overdue">final</Tag>}
              {day.isStudyDay && <Tag tone="unknown">study</Tag>}
            </div>
            <DayPanel day={day} hue={hue} onToggle={onToggle} onOpen={onOpen} />
          </div>
        ))}
      </div>
    </div>
  )
}

const isWeekend = (weekday: string) => weekday === 'SA' || weekday === 'SU'

function Tag({ tone, children }: { tone: 'overdue' | 'unknown'; children: React.ReactNode }) {
  const colour =
    tone === 'overdue'
      ? 'bg-[var(--overdue-wash)] text-[var(--overdue)]'
      : 'bg-[var(--unknown-wash)] text-[var(--unknown)]'
  return (
    <span className={`tabular rounded-full px-1.5 text-[10px] uppercase tracking-[0.06em] ${colour}`}>
      {children}
    </span>
  )
}

function Skeleton() {
  return (
    <div className="space-y-3">
      <div className="h-20 animate-pulse rounded-[var(--radius-lg)] bg-[var(--surface-sunken)]" />
      <div className="h-10 animate-pulse rounded-[var(--radius)] bg-[var(--surface-sunken)]" />
      <div className="h-64 animate-pulse rounded-[var(--radius-lg)] bg-[var(--surface-sunken)]" />
    </div>
  )
}
